/**
 * MarkerOverlay — Canvas overlay that draws marker outlines and ID labels.
 *
 * Renders on top of the camera view. Uses coordinate mapping to
 * transform detection-space coordinates to screen-space.
 * ArUco markers shown in blue, QR markers shown in green.
 */

import { useRef, useEffect } from 'react';
import { createCoordinateMapper } from './coordinateMapper';
import type { DetectedMarker } from '../detection/types';

export interface MarkerOverlayProps {
  markers: DetectedMarker[];
  videoWidth: number;
  videoHeight: number;
  detectionWidth: number;
  detectionHeight: number;
  className?: string;
}

// ArUco styling
const ARUCO_STROKE = '#00aaff';
const ARUCO_FILL = 'rgba(0, 170, 255, 0.1)';
const ARUCO_LABEL = '#00aaff';

// QR styling
const QR_STROKE = '#44cc44';
const QR_FILL = 'rgba(68, 204, 68, 0.1)';
const QR_LABEL = '#44cc44';

const STROKE_WIDTH = 3;
const FONT = 'bold 16px -apple-system, BlinkMacSystemFont, sans-serif';
const SMALL_FONT = 'bold 12px -apple-system, BlinkMacSystemFont, sans-serif';
const MAX_QR_LABEL_LEN = 20;

/** Truncate long QR data for display */
function truncateLabel(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1) + '\u2026';
}

export function MarkerOverlay({
  markers,
  videoWidth,
  videoHeight,
  detectionWidth,
  detectionHeight,
  className = '',
}: MarkerOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Size canvas to its display size
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(rect.width * dpr);
    const h = Math.round(rect.height * dpr);

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    ctx.clearRect(0, 0, w, h);

    if (markers.length === 0 || videoWidth === 0 || videoHeight === 0) return;

    ctx.save();
    ctx.scale(dpr, dpr);

    const mapper = createCoordinateMapper({
      videoWidth,
      videoHeight,
      screenWidth: rect.width,
      screenHeight: rect.height,
      detectionWidth,
      detectionHeight,
    });

    for (const marker of markers) {
      const isQr = marker.markerType === 'qr';
      const strokeColor = isQr ? QR_STROKE : ARUCO_STROKE;
      const fillColor = isQr ? QR_FILL : ARUCO_FILL;
      const labelColor = isQr ? QR_LABEL : ARUCO_LABEL;

      const screenCorners = marker.corners.map((c) => mapper.toScreen(c));

      // Draw outline
      ctx.beginPath();
      ctx.moveTo(screenCorners[0].x, screenCorners[0].y);
      for (let i = 1; i < screenCorners.length; i++) {
        ctx.lineTo(screenCorners[i].x, screenCorners[i].y);
      }
      ctx.closePath();

      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = STROKE_WIDTH;
      ctx.stroke();

      ctx.fillStyle = fillColor;
      ctx.fill();

      // Draw label at center
      const cx = screenCorners.reduce((sum, c) => sum + c.x, 0) / 4;
      const cy = screenCorners.reduce((sum, c) => sum + c.y, 0) / 4;

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (isQr) {
        // QR: show "QR" badge + truncated data
        ctx.font = SMALL_FONT;
        ctx.fillStyle = labelColor;
        ctx.fillText('QR', cx, cy - 16);
        ctx.font = SMALL_FONT;
        ctx.fillText(truncateLabel(marker.markerId, MAX_QR_LABEL_LEN), cx, cy + 2);
      } else {
        // ArUco: show "#ID"
        ctx.font = FONT;
        ctx.fillStyle = labelColor;
        ctx.fillText(`#${marker.markerId}`, cx, cy - 10);
      }
    }

    ctx.restore();
  }, [markers, videoWidth, videoHeight, detectionWidth, detectionHeight]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}
